<template>
  <div class="ha">
    <div class="tools">
      <div class="savingIndicator">
        {{ saving ? "Saving..." : "" }}
      </div>
      <span style="margin-top: auto">Title</span>
      <input v-model="title" @input="saveVisualizer" class="title" />
      <span>Description</span>
      <textarea v-model="description" @input="saveVisualizer" class="desc" />
      <span>Actions</span>
      <button @click="createNewContainer('code')" class="tool">Code</button>
      <button @click="createNewContainer('text')" class="tool">Text</button>
      <button @click="createNewContainer('input')" class="tool">Input</button>
      <button @click="createNewContainer('image')" class="tool">Image</button>
      <button @click="createNewContainer('table')" class="tool">Table</button>
      <button @click="createNewContainer('canvas')" class="tool">Canvas</button>
      <button
        v-if="!deletingVisualizer"
        @click="deletingVisualizer = true"
        class="tool deleteVisualizer"
      >
        {{ deleting ? "Deleting..." : "Delete Visualizer" }}
      </button>
      <div
        class="helpers deleteVisualizer deleterContainer"
        v-if="deletingVisualizer"
      >
        <button @click="deleteVisualizer()" class="tool helper deleter">
          Yes
        </button>
        <button @click="deletingVisualizer = false" class="tool helper deleter">
          No
        </button>
      </div>
    </div>
    <div
      style="
        height: calc(100vh - 36px);
        max-width: 1280px;
        min-width: 680px;
        width: 100%;
        border: 1px solid red;
        position: relative;
      "
    >
      <vue-draggable-resizable
        :parent="true"
        v-for="(container, index) in containers"
        :w="container.width"
        :h="container.height"
        :x="container.positionX"
        :y="container.positionY"
        @dragging="onDrag(container, ...arguments)"
        @resizing="onResize(container, ...arguments)"
        @dragstop="saveContainerPosition(container._id, ...arguments)"
        @resizestop="updateContainerSize(container._id, ...arguments)"
        :key="index"
      >
        <div
          class="container"
          :id="container._id"
          @click="selectContainer(container)"
        >
          <div class="helpers">
            <input
              class="helper-title"
              placeholder="Untitled container"
              v-model="container.title"
              @input="saveContainer(container._id, true)"
            />
            <button
              @click="deleteContainer(container._id, true)"
              class="tool helper-quick"
            >
              Delete
            </button>
          </div>
          <span
            >X: {{ container.positionX }} / Y: {{ container.positionY }}</span
          >
          <span
            >Width: {{ container.width }} / Height: {{ container.height }}</span
          >
          <codemirror
            v-if="container.type == 'code'"
            v-model="container.content"
            @input="saveContainer(container._id, true)"
            :options="cmOptions"
          />
        </div>
      </vue-draggable-resizable>
    </div>
    <div class="manager">
      <div class="selectedContainers">
        <div>Selected containers</div>
        <div v-if="!selectionA && !selectionB">No selections</div>
        <div
          class="selectionA"
          v-if="selectionA"
          :class="
            latestUpdatedSelection == 'selectionA' ? 'latestSelection' : ''
          "
        >
          <div>Selected container 1</div>
          <input
            v-model="selectionATitle"
            @input="updateLocalContainerTitle($event, selectionA._id)"
          />
          <div>Position X {{ selectionA.positionX }}</div>
          <div>Position Y {{ selectionA.positionY }}</div>
          <div>Width {{ selectionA.width }}</div>
          <div>Height {{ selectionA.height }}</div>
          <div class="helpers">
            <button @click="deleteContainer('selectionA')" class="tool helper">
              Delete
            </button>
          </div>
        </div>
        <div
          class="selectionB"
          v-if="selectionB"
          :class="
            latestUpdatedSelection == 'selectionB' ? 'latestSelection' : ''
          "
        >
          <div>Selected container 2</div>
          <input
            v-model="selectionBTitle"
            @input="updateLocalContainerTitle($event, selectionB._id)"
          />
          <div>Position X {{ selectionB.positionX }}</div>
          <div>Position Y {{ selectionB.positionY }}</div>
          <div>Width {{ selectionB.width }}</div>
          <div>Height {{ selectionB.height }}</div>
          <div class="helpers">
            <button @click="deleteContainer('selectionB')" class="tool helper">
              Delete
            </button>
          </div>
        </div>
        <button
          @click="connectContainers"
          :disabled="!selectionA || !selectionB"
          class="tool"
          style="margin-top: 5px"
        >
          Connect
        </button>
        <button
          @click="clearSelections"
          v-if="selectionA || selectionB"
          :disabled="!selectionA && !selectionB"
          class="tool clearSelections"
        >
          Clear selections
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LeaderLine from "leader-line-new";
import VueDraggableResizable from "vue-draggable-resizable";
import "vue-draggable-resizable/dist/VueDraggableResizable.css";

import { codemirror } from "vue-codemirror";
// import base style
import "codemirror/lib/codemirror.css";
// import language js
import "codemirror/mode/javascript/javascript.js";
// import theme style
import "codemirror/theme/base16-dark.css";

import {
  getVisualizer,
  createNewVisualizerContainer,
  getVisualizerContainers,
  saveVisualizer,
  deleteVisualizer,
  updateContainerData,
  createNewVisualizerPath,
  deleteContainer,
  saveContainer,
} from "./VisualizerHelpers";

export default {
  name: "Visualizer",
  async created() {
    this.visualizer = await getVisualizer(this.$route.params.visualizerId);
    if (this.visualizer.title) {
      this.title = this.visualizer.title;
    }
    if (this.visualizer.description) {
      this.description = this.visualizer.description;
    }
    const containers = await getVisualizerContainers(
      this.$route.params.visualizerId
    );
    if (containers && containers !== 1) {
      this.containers = containers;
      this.$nextTick(() => {
        this.drawPaths();
      });
    }
  },
  components: {
    VueDraggableResizable,
    codemirror,
  },
  data: function () {
    return {
      title: "",
      description: "",

      saving: false,
      deleting: false,
      deletingVisualizer: false,

      visualizer: null,
      containers: [],

      selectionA: null,
      selectionB: null,

      selectionATitle: "",
      selectionBTitle: "",

      latestUpdatedSelection: "",

      width: 0,
      height: 0,
      x: 0,
      y: 0,

      line: null,

      cmOptions: {
        tabSize: 4,
        mode: "text/javascript",
        theme: "base16-dark",
        lineNumbers: true,
        line: true,
        // more CodeMirror options...
      },
    };
  },
  beforeDestroy() {
    this.clearAllLines();
  },
  methods: {
    clearAllLines() {
      for (var i = 0; i < this.containers.length; i++) {
        let container = this.containers[i];
        let paths = container.paths;
        for (var x = paths.length - 1; x > -1; x--) {
          let pathX = paths[x];
          pathX.line.remove();
          pathX.line = null;
        }
      }
    },

    createNewLine(start, end) {
      return new LeaderLine(
        document.getElementById(`${start}`),
        document.getElementById(`${end}`)
      );
    },

    clearSelections() {
      this.selectionA = null;
      this.selectionB = null;
      this.selectionATitle = "";
      this.selectionBTitle = "";
      this.latestUpdatedSelection = "";
    },

    async saveVisualizer() {
      this.saving = true;
      const data = {
        visualizerId: this.visualizer._id,
        title: this.title,
        description: this.description,
      };
      await saveVisualizer(data);
      let containerIds = [];
      for (var i = 0; i < this.containers.length; i++) {
        const containerId = this.containers[i]._id;
        containerIds.push(containerId);
      }
      for (const id of containerIds) {
        await this.saveContainer(id, true);
      }
      this.saving = false;
    },

    async deleteVisualizer() {
      this.deleteVisualizer = false;
      this.deleting = true;
      const data = {
        visualizerId: this.visualizer._id,
      };
      await deleteVisualizer(data);
      this.clearAllLines();
      this.deleting = false;
      this.$router.push("/");
    },

    selectContainer(container) {
      /* If already selected */
      let A = this.selectionA;
      let B = this.selectionB;
      const isSelectionA = A && A._id == container._id;
      const isSelectionB = B && B._id == container._id;
      if (isSelectionA || isSelectionB) {
        console.log("already selected");
        if (isSelectionA) {
          this.latestUpdatedSelection = "selectionA";
        } else {
          this.latestUpdatedSelection = "selectionB";
        }
        return;
      }

      /**
       * First click = A
       * Second click = B
       * Third click = A
       * Forth click = B
       * Could track clicks && use odd/even.
       */
      if (!A && !B) {
        A = container;
        if (container.title) {
          this.selectionATitle = container.title;
        } else {
          this.selectionATitle = "Untitled container";
        }
        this.latestUpdatedSelection = "selectionA";
      } else if (A && !B) {
        B = container;
        if (container.title) {
          this.selectionBTitle = container.title;
        } else {
          this.selectionBTitle = "Untitled container";
        }
        this.latestUpdatedSelection = "selectionB";
      } else if (A && B && this.latestUpdatedSelection === "selectionB") {
        A = container;
        if (container.title) {
          this.selectionATitle = container.title;
        } else {
          this.selectionATitle = "Untitled container";
        }
        this.latestUpdatedSelection = "selectionA";
      } else if (A && B && this.latestUpdatedSelection === "selectionA") {
        B = container;
        if (container.title) {
          this.selectionBTitle = container.title;
        } else {
          this.selectionBTitle = "Untitled container";
        }
        this.latestUpdatedSelection = "selectionB";
      }
      this.selectionA = A;
      this.selectionB = B;
    },

    async connectContainers() {
      const startId = this.selectionA._id;
      const endId = this.selectionB._id;
      const line = this.createNewLine(startId, endId);
      const path = { startId, endId, line };

      for (var i = 0; i < this.containers.length; i++) {
        let container = this.containers[i];
        if (container._id == startId) {
          container.paths.push(path);
        }
        if (container._id == endId) {
          container.paths.push(path);
        }
      }
      this.clearSelections();
      await createNewVisualizerPath(path);
    },

    drawPaths() {
      if (!this.containers || !this.containers.length) {
        console.log("oh oops");
      }
      for (var i = 0; i < this.containers.length; i++) {
        const container = this.containers[i];
        for (var j = 0; j < container.paths.length; j++) {
          let path = container.paths[j];
          // path.line = this.createNewLine(path.startId, path.endId);
          if (container._id == path.startId) {
            const commonLine = this.createNewLine(path.startId, path.endId);
            if (path.line) {
              path.line.remove();
              path.line = null;
            }
            path.line = commonLine;
            this.addLineToEndPath(path.startId, path.endId, commonLine);
          }
        }
      }
    },

    addLineToEndPath(startId, endId, line) {
      for (var i = 0; i < this.containers.length; i++) {
        const container = this.containers[i];
        if (container._id == endId) {
          for (var j = 0; j < container.paths.length; j++) {
            const path = container.paths[j];
            if (path.startId == startId) {
              // path.line = line;
              console.log(line);
              path.line = this.createNewLine(path.startId, path.endId);
            }
          }
        }
      }
    },

    async updateContainerSize(id, positionX, positionY, width, height) {
      console.log(id, positionX, positionY, width, height);
      let container = null;
      for (var i = 0; i < this.containers.length; i++) {
        let c = this.containers[i];
        if (c._id == id) {
          c.width = width;
          c.height = height;
          c.positionX = positionX;
          c.positionY = positionY;
          container = c;
        }
      }
      await this.updateContainerData(container);
    },

    async saveContainerPosition(containerId, positionX, positionY) {
      console.log(containerId, positionX, positionY);
      let container = null;
      for (var i = 0; i < this.containers.length; i++) {
        let c = this.containers[i];
        if (c._id == containerId) {
          c.positionX = positionX;
          c.positionY = positionY;
          container = c;
        }
      }
      await this.updateContainerData(container);
    },

    async updateContainerData(container) {
      container = JSON.parse(JSON.stringify(container));
      await updateContainerData(container);
      // this.drawPaths();
    },

    async createNewContainer(type) {
      const visualizerId = this.$route.params.visualizerId;
      const container = await createNewVisualizerContainer(visualizerId, type);
      this.containers.push(container);
    },

    updateLocalContainerTitle($event, containerId) {
      for (var i = 0; i < this.containers.length; i++) {
        let container = this.containers[i];
        if (container._id == containerId) {
          container.title = $event.target.value;
          break;
        }
      }
      this.saveContainer(containerId, true);
    },

    async saveContainer(target, direct = false) {
      this.saving = true;
      let containerId = null;
      if (direct) {
        containerId = target;
      } else {
        if (target == "selectionA") {
          containerId = this.selectionA._id;
        } else if (target == "selectionB") {
          containerId = this.selectionB._id;
        }
      }
      let container = null;
      for (var i = 0; i < this.containers.length; i++) {
        let cont = this.containers[i];
        if (cont._id == containerId) {
          container = cont;
          break;
        }
      }

      container = JSON.parse(JSON.stringify(container));
      await saveContainer(container);
      this.saving = false;
    },

    async deleteContainer(target, direct = false) {
      let containerId = null;
      if (direct) {
        containerId = target;
      } else {
        if (target == "selectionA") {
          containerId = this.selectionA._id;
        } else if (target == "selectionB") {
          containerId = this.selectionB._id;
        }
      }
      await deleteContainer(containerId);
      for (var i = 0; i < this.containers.length; i++) {
        let container = this.containers[i];
        if (container._id == containerId) {
          for (var x = container.paths.length - 1; x > -1; x--) {
            let pathX = container.paths[x];
            pathX.line.remove();
            pathX.line = null;
          }
          this.containers.splice(i, 1);
        } else {
          let paths = container.paths;
          for (var j = paths.length - 1; j > -1; j--) {
            let path = paths[j];
            if (path.startId == containerId || path.endId == containerId) {
              path.line.remove();
              path.line == null;
              paths.splice(j, 1);
            }
          }
        }
      }
    },

    onResize: function (container, x, y, width, height) {
      // console.log(x, y, width, height);
      container.positionX = x;
      container.positionY = y;
      container.width = width;
      container.height = height;
      // this.redrawLine(container);
      // this.drawPaths();
      this.drawOnMove(container);
    },

    onDrag: function (container, x, y) {
      // console.log(x, y);
      container.positionX = x;
      container.positionY = y;
      // this.redrawLine(container);
      // this.drawPaths();
      this.drawOnMove(container);
    },

    drawOnMove(container) {
      for (var i = 0; i < container.paths.length; i++) {
        let path = container.paths[i];
        let otherId = null;
        let originalType = "";
        if (container._id == path.startId) {
          otherId = path.endId;
          originalType = "start";
        } else if (container._id == path.endId) {
          otherId = path.startId;
          originalType = "end";
        }
        this.removeLineFromPartnerLine(otherId, container._id, originalType);
        path.line.remove();
        path.line = null;
        path.line = this.createNewLine(path.startId, path.endId);
      }
    },

    redrawLine(container) {
      /** Go over the container's paths & redraw all lines where it is the start position of a path; where it's not, redraw it in the other one. */
      for (var i = 0; i < container.paths.length; i++) {
        /* Ensure no double lines */
        let path = container.paths[i];
        path.line.remove();
        path.line = null;
        let originalType = "";
        let partnerContainerId = "";
        if (path.startId == container._id) {
          originalType = "start";
          partnerContainerId = path.endId;
        } else {
          originalType = "end";
          partnerContainerId = path.startId;
        }

        /* Redraw line for whichever container is being moved; remove for the other one. */
        path.line = this.createNewLine(path.startId, path.endId);
        this.removeLineFromPartnerLine(
          partnerContainerId,
          container._id,
          originalType
        );
        // this.addLineToEndPath(path.startId, path.endId, path.line);
      }
    },

    removeLineFromPartnerLine(
      partnerContainerId,
      originalContainerId,
      originalType
    ) {
      for (var i = 0; i < this.containers.length; i++) {
        const container = this.containers[i];
        if (container._id == partnerContainerId) {
          for (var j = 0; j < container.paths.length; j++) {
            const path = container.paths[j];
            if (
              (originalType == "start" &&
                path.endId == partnerContainerId &&
                path.startId == originalContainerId &&
                path.line) ||
              (originalType == "end" &&
                path.startId == partnerContainerId &&
                path.endId == originalContainerId &&
                path.line)
            ) {
              path.line.remove();
              path.line = null;
              if (originalType == "start") {
                path.line = this.createNewLine(
                  originalContainerId,
                  partnerContainerId
                );
              } else {
                path.line = this.createNewLine(
                  partnerContainerId,
                  originalContainerId
                );
              }
            }
          }
        }
      }
    },
  },
  watch: {
    se: function (newVal, oldVal) {
      console.log("value changed from " + oldVal + " to " + newVal);
    },
  },
};
</script>

<style scoped>
.ha {
  display: flex;
}

.tools,
.manager {
  display: flex;
  flex-direction: column;
  width: 200px;
  min-width: 200px;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 36px);
}

.tool {
  margin-bottom: 15px;
  width: 150px;
  cursor: pointer;
  font-size: 20px;
}

.helpers {
  display: flex;
  align-items: center;
}

.helper {
  font-size: 16px;
  width: 60px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 10px;
  margin-bottom: 2px;
}

.helper-title {
  margin-right: auto;
  border: none;
  font-size: 16px;
  font-weight: 600;
}

.helper-quick {
  font-size: 14px;
  width: 55px;
  margin: 2px 3px;
  border: none;
}

.tools {
  border: 2px solid rgb(70, 225, 65);
}

.manager {
  border: 2px solid slateblue;
}

div#start,
div#end {
  display: block;
  width: 125px;
  height: 25px;
  border: 2px solid green;
}

#end {
  margin-left: 150px;
}

.selectedContainers {
  border: 1px solid;
  padding: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.selectionA,
.selectionB {
  border: 1px solid;
  padding: 4px;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  margin-top: 5px;
}

.selectionA {
  margin-bottom: 4px;
}

.clearSelections {
  font-size: 16px;
}

.container {
  width: 100%;
  height: 100%;
}
.CodeMirror {
  min-height: 50px !important;
  max-height: 500px !important;
}

.latestSelection {
  border: 2px solid blue;
}

.title {
  margin-bottom: 5px;
}

.desc {
  margin-bottom: 25px;
}

.deleteVisualizer {
  font-size: 14px;
  background-color: #e4cdcd;
  border: 1px solid;
  margin-top: auto;
}

.deleterContainer {
  width: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.deleter {
  margin: 6px 4px;
}

.savingIndicator {
  height: 20px;
  margin-bottom: auto;
  margin-top: 5px;
}
</style>